import { gbp, ukDate } from "../../lib/api";

export const unitKind = (unit) => {
  const u = String(unit || "").trim().toLowerCase().split(" ")[0];
  return ["each", "ea", "piece", "pc", "pcs", "unit", "item", "can", "bottle", "btl", "tin", "jar", "portion", "drum", "bag", "tub", "pot", "cup"].includes(u) ? "item" : "unit";
};

// Default purchase unit of an offer: the unit its latest price is quoted in, if that unit has an invoiced price; else the first priced one; else the primary lane (so a confirmed-but-unpriced lane is still orderable).
export const defaultPurchaseUnit = (option) => {
  const units = option.purchase_units || [];
  if (!units.length) return null;
  const priced = units.filter((u) => u.price);
  if (priced.length) return (priced.find((u) => u.key === unitKind(option.unit)) || priced[0]).key;
  return (units.find((u) => u.key === unitKind(option.unit)) || units[0]).key;
};

const STALE_DAYS = 45;
const daysAgo = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

// Mirrors services/purchase_units.box_saving — only exact multiples, only priced, only when it really saves.
export function boxSaving(units, qty, selectedKey) {
  const box = units.find((u) => u.key === "unit"), each = units.find((u) => u.key === "item");
  if (selectedKey !== "item" || !box?.price || !each?.price || !box.items_per_unit || qty < box.items_per_unit || qty % box.items_per_unit !== 0) return null;
  const boxes = qty / box.items_per_unit;
  const saving = Math.round((qty * each.price - boxes * box.price) * 100) / 100;
  return saving > 0 ? { boxes, saving, box, each } : null;
}

export function PurchaseUnitPicker({ units, value, onChange, testId }) {
  if (!units || units.length < 2) return null;
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" role="radiogroup" aria-label="Purchase unit" data-testid={testId}>
      {units.map((u) => (
        <button key={u.key} type="button" role="radio" aria-checked={value === u.key} onClick={() => onChange(u.key)}
          title={u.price ? `${gbp(u.price)} per ${u.label} · ${u.source === "price_list" ? "Manual Uploaded Price" : "last invoiced"} ${ukDate(u.price_date)}` : `No ${u.label} price yet — supplier will confirm`}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold ${value === u.key ? "bg-white shadow text-slate-900" : "text-slate-500"}`} data-testid={`${testId}-${u.key}`}>
          {u.label.toUpperCase()}{u.price ? "" : " (no price yet)"}
        </button>
      ))}
    </div>
  );
}

export function BoxSavingHint({ saving, onSwitch, testId }) {
  if (!saving) return null;
  const { boxes, box, each } = saving;
  const stale = [box.price_date, each.price_date].some((d) => (daysAgo(d) ?? 9999) > STALE_DAYS);
  return (
    <div className="mt-1.5 text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5" data-testid={testId}>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold" data-testid={`${testId}-text`}>Save {gbp(saving.saving)} by buying {boxes} {box.label.toUpperCase()} instead of {saving.boxes * box.items_per_unit} {each.label.toUpperCase()}.</span>
        <button type="button" onClick={onSwitch} className="font-semibold underline" data-testid={`${testId}-switch`}>Switch to {box.label.toUpperCase()}</button>
      </p>
      <p className="text-emerald-800/80 font-mono" data-testid={`${testId}-prices`}>{box.label.toUpperCase()} price: {gbp(box.price)} — {box.source === "price_list" ? "Manual Uploaded Price" : "last invoiced"} {ukDate(box.price_date)} · {each.label.toUpperCase()} price: {gbp(each.price)} — {each.source === "price_list" ? "Manual Uploaded Price" : "last invoiced"} {ukDate(each.price_date)}</p>
      {stale && <p className="text-amber-800" data-testid={`${testId}-stale`}>Based on historical invoice prices, not confirmed current supplier prices.</p>}
    </div>
  );
}
